// Month-six decision history for the superintendent uptake read and the daily
// report decision record (vision-run uptake-seed #311 / #302). Actions hang off
// real advisory cases — no generated case list in CASES.

import type { RecommendedAction } from '@/versions/v1/schema/case';
import type { PlantRoleId } from '@/versions/v1/schema/plant-role';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import type { SeatId } from '@/versions/v1/schema/seat';
import { addMinutes } from '@/versions/v1/shared/clock.derive';

/** Case raisedAt stamps — decisions are always offset from the case clock, not a spec clock. */
const CASE_RAISED_AT: Record<string, SeriesTime> = {
  'case-pb-ph-closed': '2026-06-07T22:10',
  'case-lime-trip': '2026-06-07T20:45',
  'case-zn-ph-response': '2026-06-08T06:42',
  'case-cuso4': '2026-06-08T07:30',
  'case-lic': '2026-06-08T08:40',
  'case-zn-cond-closed': '2026-06-08T06:40',
  'case-mibc-closed': '2026-06-08T08:30',
  'case-ag-grade': '2026-06-08T04:10',
  'case-zn-recovery': '2026-06-08T06:50',
  'case-rg-power': '2026-06-08T02:15',
  'case-p80': '2026-06-08T03:15',
  'case-belt-closed': '2026-06-08T08:05',
  'case-belt-scale': '2026-06-08T09:30',
  'case-tramp': '2026-06-08T09:10',
  'case-pb-air-step': '2026-06-07T21:30',
  'case-cyc-density': '2026-06-07T23:00',
  'case-cyclone-closed': '2026-06-08T05:20',
  'case-thickener-bed': '2026-06-07T23:50',
  'case-air': '2026-06-08T08:05',
};

type ActionSeed = {
  id: string;
  caseId: string;
  text: string;
  status: 'done' | 'approved' | 'rejected' | 'proposed';
  decidedOffsetMin: number;
  implementedOffsetMin?: number;
  reason?: string;
  constraint?: { readingId: string };
};

function caseRaisedAt(caseId: string): SeriesTime {
  const stamp = CASE_RAISED_AT[caseId];
  if (!stamp) throw new Error(`uptake seed: missing CASE_RAISED_AT for ${caseId}`);
  return stamp;
}

function seatForRole(role: PlantRoleId): SeatId {
  if (role === 'cro') return 'seat-cro';
  if (role === 'maintenance') return 'seat-maintenance';
  if (role === 'operator') return 'seat-operator';
  if (role === 'ops-supervisor') return 'seat-supt';
  return 'seat-met';
}

/** Hand-seeded latencies — per-role bands so no two uptake medians collide. */
const DECISION_OFFSETS: Record<PlantRoleId, readonly number[]> = {
  cro: [18, 42, 68, 95, 125, 155],
  metallurgist: [22, 48, 75, 105, 140, 175],
  'senior-metallurgist': [],
  maintenance: [35, 72, 110, 150, 185, 220],
  operator: [20, 50, 80, 115, 145, 175],
  'ops-supervisor': [25, 55, 88, 120, 155, 190],
  gm: [30, 60, 90, 120, 150, 180],
  superintendent: [30, 60, 90, 120, 150, 180],
};

const IMPLEMENT_OFFSETS: Record<PlantRoleId, readonly number[]> = {
  cro: [12, 22, 28, 35, 40],
  metallurgist: [28, 38, 52, 65, 70],
  'senior-metallurgist': [],
  maintenance: [48, 72, 95, 120, 150],
  operator: [18, 28, 38, 45, 50],
  'ops-supervisor': [22, 32, 45, 52, 55],
  gm: [30, 45, 60, 75, 90],
  superintendent: [30, 45, 60, 75, 90],
};

function buildRoleShiftActions(
  role: PlantRoleId,
  shift: 'night' | 'day',
  specs: readonly {
    caseId: string;
    text: string;
    status: ActionSeed['status'];
    id?: string;
    constraint?: { readingId: string };
  }[],
): ActionSeed[] {
  const decide = DECISION_OFFSETS[role];
  const implement = IMPLEMENT_OFFSETS[role];
  return specs.map((spec, i) => ({
    id: spec.id ?? `ra-uptake-${role}-${shift}-${i + 1}`,
    caseId: spec.caseId,
    text: spec.text,
    status: spec.status,
    constraint: spec.constraint,
    decidedOffsetMin: decide[i % decide.length]!,
    implementedOffsetMin:
      spec.status === 'done' ? decide[i % decide.length]! + implement[i % implement.length]! : undefined,
    reason:
      spec.status === 'rejected'
        ? 'Hold — within SP band; revisit at shift handover.' // [syn]
        : undefined,
  }));
}

const NIGHT_CRO = buildRoleShiftActions('cro', 'night', [
  {
    caseId: 'case-pb-ph-closed',
    text: 'Raise lime SP on Pb rougher — pH dipped below 10.5 band',
    status: 'done',
  },
  {
    caseId: 'case-lime-trip',
    text: 'Restart lime dosing pump LIM-402 — trip cleared after 8 min',
    status: 'done',
  },
  {
    caseId: 'case-lime-trip',
    text: 'Verify lime loop AIC5002 in Auto after pump restart',
    status: 'done',
  },
  {
    caseId: 'case-pb-ph-closed',
    text: 'Confirm Pb rougher pH stable after lime SP bump',
    status: 'rejected',
  },
  {
    caseId: 'case-lime-trip',
    text: 'Step lime flow +5% — catch-up dose after trip',
    status: 'done',
  },
  {
    caseId: 'case-pb-ph-closed',
    text: 'Log lime consumption vs design for night shift handover',
    status: 'done',
  },
]);

const DAY_CRO = buildRoleShiftActions('cro', 'day', [
  {
    caseId: 'case-zn-ph-response',
    text: 'Increase lime SP to Zn circuit — pH 10.2 below band',
    status: 'done',
  },
  {
    caseId: 'case-zn-ph-response',
    text: 'Confirm Zn float pH inside 10.8–11.2 after lime step',
    status: 'done',
  },
  {
    caseId: 'case-cuso4',
    text: 'Arrange CuSO₄ solution-strength titration — activation ambiguity',
    status: 'approved',
  },
  {
    caseId: 'case-zn-ph-response',
    text: 'Trim blower air on Zn rougher — froth depth high',
    status: 'done',
  },
  {
    caseId: 'case-cuso4',
    text: 'Check CuSO₄ pump stroke — FIT reads low',
    status: 'rejected',
  },
  {
    caseId: 'case-zn-ph-response',
    text: 'Hold lime SP — pH stable 68% of shift',
    status: 'done',
  },
]);

const NIGHT_MET = buildRoleShiftActions('metallurgist', 'night', [
  {
    caseId: 'case-thickener-bed',
    text: 'Raise thickener underflow rate — bed level 12% above target',
    status: 'done',
  },
  {
    caseId: 'case-thickener-bed',
    text: 'Verify rake torque after UF rate step',
    status: 'done',
  },
  {
    caseId: 'case-thickener-bed',
    text: 'Review Zn conditioner residence time — night shift log',
    status: 'done',
  },
  {
    caseId: 'case-thickener-bed',
    text: 'Step CuSO₄ dose to 160 g/t — activation margin overnight',
    status: 'rejected',
  },
  {
    caseId: 'case-thickener-bed',
    text: 'Confirm thickener bed level after UF step',
    status: 'done',
  },
  {
    caseId: 'case-thickener-bed',
    text: 'Log thickener trend for day-shift handover',
    status: 'done',
  },
]);

const DAY_MET = buildRoleShiftActions('metallurgist', 'day', [
  {
    id: 'ra-uptake-raise-threshold',
    caseId: 'case-ag-grade',
    text: 'Increase 3418A dosage to 45 g/t — Ag recovery gap',
    status: 'approved',
  },
  {
    caseId: 'case-zn-recovery',
    text: 'Increase lime to Zn circuit — target pH 11.0–11.2',
    status: 'done',
  },
  {
    caseId: 'case-ag-grade',
    text: 'Review Zn conditioner residence time — recovery lag',
    status: 'proposed',
  },
  {
    caseId: 'case-zn-recovery',
    text: 'Check SMBS dosage in Pb circuit for carry-over',
    status: 'done',
  },
  {
    caseId: 'case-ag-grade',
    text: 'Calibrate Pb/Ag thickener density instrument — Ag mass balance',
    status: 'proposed',
  },
  {
    caseId: 'case-zn-recovery',
    text: 'Hold lime SP — Zn float pH held at 11.1 · recovery unchanged',
    status: 'rejected',
  },
]);

const NIGHT_MAINT = buildRoleShiftActions('maintenance', 'night', [
  {
    caseId: 'case-rg-power',
    text: 'Inspect regrind mill motor — power dip to 78% load',
    status: 'done',
  },
  {
    caseId: 'case-rg-power',
    text: 'Verify regrind cyclone apex — feed density drift overnight',
    status: 'done',
  },
  {
    caseId: 'case-p80',
    text: 'Increase cyclone feed pressure to 88–90 kPa — P80 gap',
    status: 'rejected',
  },
  {
    caseId: 'case-rg-power',
    text: 'Schedule ball mill liner inspection — energy deviation',
    status: 'done',
  },
  {
    caseId: 'case-p80',
    text: 'Check cyclone apex wear — pressure at low end of SP',
    status: 'done',
  },
  {
    caseId: 'case-rg-power',
    text: 'Log regrind power trend for day-shift handover',
    status: 'rejected',
  },
]);

const DAY_MAINT = buildRoleShiftActions('maintenance', 'day', [
  {
    caseId: 'case-belt-closed',
    text: 'Recalibrate WIT-014 belt scale — 4.2% high vs density meter',
    status: 'done',
  },
  {
    caseId: 'case-tramp',
    text: 'Inspect CV-001 magnet — tramp iron event',
    status: 'approved',
  },
  {
    caseId: 'case-belt-closed',
    text: 'Verify screen panel on trash screen — wear check',
    status: 'done',
  },
  {
    caseId: 'case-belt-closed',
    text: 'Schedule cyclone inspection — apex and vortex finder',
    status: 'rejected',
  },
  {
    caseId: 'case-tramp',
    text: 'Log magnet pull test result for reliability review',
    status: 'done',
  },
  {
    caseId: 'case-belt-closed',
    text: 'Confirm WIT-014 calibration held after recalibration',
    status: 'done',
  },
]);

const NIGHT_OPS = buildRoleShiftActions('ops-supervisor', 'night', [
  {
    caseId: 'case-pb-air-step',
    text: 'Step Pb rougher air rate +4% — froth depth low overnight',
    status: 'done',
  },
  {
    caseId: 'case-pb-air-step',
    text: 'Confirm crew round completion — night shift compliance',
    status: 'done',
  },
  {
    id: 'ra-uptake-night-open',
    caseId: 'case-pb-air-step',
    text: 'Raise with metallurgist — Zn conditioner pH still soft',
    status: 'proposed',
  },
  {
    caseId: 'case-pb-air-step',
    text: 'Trim fresh-feed rate −2% — mill constraint overnight',
    status: 'done',
  },
  {
    caseId: 'case-pb-air-step',
    text: 'Verify shift sample schedule — 3 LIMS pending',
    status: 'rejected',
  },
  {
    caseId: 'case-pb-air-step',
    text: 'Log shift tonnes vs target for handover',
    status: 'done',
  },
]);

const DAY_OPS = buildRoleShiftActions('ops-supervisor', 'day', [
  {
    caseId: 'case-belt-scale',
    text: 'Cross-check belt scale against density meter — drift watch',
    status: 'done',
  },
  {
    caseId: 'case-belt-scale',
    text: 'Confirm shift compliance sample schedule on track',
    status: 'done',
  },
  {
    caseId: 'case-belt-scale',
    text: 'Escalate tramp magnet walk-out — CV-001 overdue',
    status: 'approved',
  },
  {
    caseId: 'case-belt-scale',
    text: 'Review shift tonnes vs 900 t target',
    status: 'done',
  },
  {
    caseId: 'case-belt-scale',
    text: 'Hold feed rate — mill at constraint',
    status: 'rejected',
  },
  {
    caseId: 'case-belt-scale',
    text: 'Brief day crew on lime pump trip overnight',
    status: 'done',
  },
]);

const NIGHT_OP = buildRoleShiftActions('operator', 'night', [
  {
    caseId: 'case-cyc-density',
    text: 'Trim cyclone feed density — drift above SP overnight',
    status: 'done',
  },
  {
    caseId: 'case-cyc-density',
    text: 'Verify dart valve stroke on Pb rougher cell 3',
    status: 'done',
  },
  {
    caseId: 'case-cyc-density',
    text: 'Adjust cyclone pressure SP −2 kPa — density back in band',
    status: 'done',
  },
  {
    caseId: 'case-cyc-density',
    text: 'Walk out to CV-001 magnet — report condition',
    status: 'rejected',
  },
  {
    caseId: 'case-cyc-density',
    text: 'Confirm froth depth on Zn rougher within band',
    status: 'done',
  },
  {
    caseId: 'case-cyc-density',
    text: 'Log cyclone feed density for shift handover',
    status: 'done',
  },
]);

const DAY_OP = buildRoleShiftActions('operator', 'day', [
  {
    id: 'ra-cuso4',
    caseId: 'case-cuso4',
    text: 'Increase CuSO₄ dose to 160 g/t — check Pb in Zn concentrate first',
    status: 'proposed',
    constraint: { readingId: 'rd-spec-zn-pb' },
  },
  {
    caseId: 'case-air',
    text: 'Open Zn rougher air valve — FIT zero with blower running',
    status: 'done',
  },
  {
    caseId: 'case-lic',
    text: 'Return LIC4010 to Auto — Pb rougher level held manual',
    status: 'done',
  },
  {
    caseId: 'case-cuso4',
    text: 'Walk out to CV-001 magnet — report condition',
    status: 'rejected',
  },
  {
    caseId: 'case-air',
    text: 'Confirm air flow restored on Zn rougher bank',
    status: 'done',
  },
  {
    caseId: 'case-lic',
    text: 'Verify froth depth on Zn rougher within band',
    status: 'done',
  },
]);

function toRecommendedAction(spec: ActionSeed, role: PlantRoleId): RecommendedAction {
  const base = {
    id: spec.id,
    caseId: spec.caseId,
    sequence: null as number | null,
    urgency: 'today' as const,
    text: spec.text,
  };
  if (spec.status === 'proposed') {
    return {
      ...base,
      status: 'proposed' as const,
      ...(spec.constraint ? { constraint: spec.constraint } : {}),
    };
  }
  const raisedAt = caseRaisedAt(spec.caseId);
  const decidedAt = addMinutes(raisedAt, spec.decidedOffsetMin);
  const seat = seatForRole(role);
  if (spec.status === 'rejected') {
    return {
      ...base,
      status: 'rejected' as const,
      decision: { seat, at: decidedAt, reason: spec.reason! },
    };
  }
  if (spec.status === 'approved') {
    return {
      ...base,
      status: 'approved' as const,
      decision: { seat, at: decidedAt },
    };
  }
  return {
    ...base,
    status: 'done' as const,
    decision: { seat, at: decidedAt },
    implementedAt: addMinutes(raisedAt, spec.implementedOffsetMin!),
  };
}

const ALL_SPECS: { role: PlantRoleId; specs: ActionSeed[] }[] = [
  { role: 'cro', specs: [...NIGHT_CRO, ...DAY_CRO] },
  { role: 'metallurgist', specs: [...NIGHT_MET, ...DAY_MET] },
  { role: 'maintenance', specs: [...NIGHT_MAINT, ...DAY_MAINT] },
  { role: 'ops-supervisor', specs: [...NIGHT_OPS, ...DAY_OPS] },
  { role: 'operator', specs: [...NIGHT_OP, ...DAY_OP] },
];

export const UPTAKE_HISTORY_ACTIONS: readonly RecommendedAction[] = ALL_SPECS.flatMap(({ role, specs }) =>
  specs.map((spec) => toRecommendedAction(spec, role)),
);
